import { AppShell } from '@/components/app-shell';
import { OnboardingCheck } from '@/components/onboarding-check.component';
import { AuthRouter } from '@/components/auth/auth-router.component';
import { ClerkTicketHandler } from '@/components/clerk-ticket-handler.component';
import { SIGN_UP_PATH, ticketNeedsSignUp } from '@/lib/clerk-ticket';
import { CommandPalette } from '@/components/command-palette';
import { MaintenanceGate } from '@/components/maintenance-gate.component';
import { MobileBlocker } from '@/components/mobile-blocker.component';
import { isClerkOAuthHandoffTarget } from '@/lib/clerk-oauth-handoff';
import {
  consumePostAuthRedirect,
  markSignedInThisPage,
} from '@/lib/post-auth-redirect';
import { SlackLinkConsent } from '@/containers/integrations/slack-link-consent.component';
import { TrialAccessBoundary } from '@/components/trial/trial-access-boundary.component';
import { useOnboardingReturnRedirect } from '@/containers/onboarding/use-onboarding-return-redirect.hook';
import { useIsOnboardingRoute } from '@/hooks/use-is-onboarding-route.hook';
import { useExtensionCookie } from '@/hooks/use-extension-cookie.hook';
import { RootError } from '@/containers/root/root-error.component';
import { shouldBypassTrialBoundary } from '@/lib/trial-route-policy';
import { store } from '@/store';
import { selectTheme } from '@/store/features/theme.feature';
import { ClerkFailed, ClerkLoading, Show, useUser } from '@clerk/react';
import { reportError } from '@/lib/telemetry';
import {
  createRootRoute,
  Outlet,
  useLocation,
  useRouterState,
} from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { pageviewUrl } from '@/lib/pageview-url';
import { useEffect, useState, type ErrorInfo } from 'react';
import { Provider, useSelector } from 'react-redux';

const RootContent = () => {
  const theme = useSelector(selectTheme);
  const { pathname, searchStr } = useLocation();
  // Back from a connector's sign-in or Stripe Checkout mid-onboarding.
  useOnboardingReturnRedirect();
  const isAuthRoute = pathname.startsWith('/auth');
  const isOnboardingRoute = useIsOnboardingRoute();
  // Routes that render bare (no AppShell, no AuthRouter redirect) — used
  // for callback / handoff pages that should not be guarded by auth.
  const isMcpAuthCallback = pathname === '/mcp-auth-callback';
  // AppSumo activation runs a custom flow (Clerk signup inline + OAuth
  // round-trip with the API) and uses its own AuthLayout. It must
  // render bare in BOTH states: signed-out users need the inline
  // SignUp widget without being redirected by AuthRouter, and
  // signed-in users shouldn't have the AppShell sidebar wrap the page.
  const isAppsumoActivateRoute = pathname.startsWith('/appsumo/activate');
  // Public pages that never read Clerk: they still render when its script
  // fails to load (an ad blocker), instead of the sign-in failure screen.
  const worksWithoutClerk =
    pathname === '/extension-feedback' || pathname === '/crm/failed';
  const isPublicRoute =
    worksWithoutClerk || isMcpAuthCallback || isAppsumoActivateRoute;
  // The mailbox sign-in's return window renders alone, without the app around
  // it: it only reports back to the page that opened it and closes.
  const isConnectReturn =
    pathname === '/settings/email-accounts' &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('popup');
  const bypassesTrialBoundary = shouldBypassTrialBoundary(pathname);
  // A ticket shows "Signing you in..." while ClerkTicketHandler consumes it,
  // except an invitation on the sign-up page, where <SignUp/> consumes it.
  const hasClerkTicket =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('__clerk_ticket') &&
    !(pathname === SIGN_UP_PATH && ticketNeedsSignUp(window.location.search));
  const posthog = usePostHog();
  const { isSignedIn } = useUser();
  // Slack callback values are captured before telemetry and completed here so
  // auth, onboarding, and route redirects cannot strand the one-time token.

  useEffect(() => {
    if (pathname && posthog && isSignedIn) {
      // searchStr is the raw "?a=b" query (the parsed `search` object's
      // toString() logged every pageview as "?[object Object]"), filtered to
      // an allowlist so Clerk and OAuth tokens never reach analytics.
      posthog?.capture('$pageview', {
        $current_url: pageviewUrl(window.origin, pathname, searchStr),
      });
    }
  }, [pathname, searchStr, posthog, isSignedIn]);

  // Signed in. Clerk's own redirect after sign-in is a full page load the auth
  // pages' useAuthRedirect never sees, so this is the one place a sign-in is
  // reliably seen. Mark the browser as a returning user's, so a later
  // signed-out visit opens Login rather than Register, and clear the link a
  // signed-out visit was bounced from (Clerk has opened it) so a later
  // sign-in in this tab doesn't reopen it.
  useEffect(() => {
    if (!isSignedIn) return;
    try {
      localStorage.setItem('cleanlist_has_authenticated', 'true');
    } catch {
      // Storage blocked: a signed-out visit offers Register, as before.
    }
    consumePostAuthRedirect();
    markSignedInThisPage();
  }, [isSignedIn]);

  useExtensionCookie();

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Clerk's OAuth flow bounces users to the sign-in URL (this app) with a
  // `redirect_url` back into the flow: Clerk's own hosts (carrying a
  // __clerk_db_jwt resume token), or our /oauth-consent page. Forward to it
  // as soon as we land on any route with that param so the OAuth round-trip
  // can finish. Doesn't gate on isSignedIn — the target handles the sign-in
  // if needed, then completes the consent flow.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get('redirect_url');
    if (!redirectUrl) return;
    try {
      const target = new URL(redirectUrl);
      const allowed = isClerkOAuthHandoffTarget(target, window.location.origin);
      console.log('[clerk-oauth-handoff]', {
        hostname: target.hostname,
        allowed,
      });
      if (allowed) {
        window.location.replace(redirectUrl);
      }
    } catch (err) {
      console.warn('[clerk-oauth-handoff] malformed redirect_url', err);
    }
  }, [pathname, searchStr]);

  // Clerk's OAuth consent page is a step in another app's sign-in (an MCP
  // client connecting): no app shell, auth redirect, trial gate or mobile
  // blocker. The route signs people in itself.
  if (pathname === '/oauth-consent' || isConnectReturn) return <Outlet />;

  return (
    <>
      <ClerkTicketHandler />
      <SlackLinkConsent />
      {/* <Show> renders nothing until Clerk loads, and forever if its script
          is blocked: a blank page on every slow cold load (Replay Vision). */}
      <ClerkLoading>
        <div className='flex min-h-screen items-center justify-center bg-background'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent' />
        </div>
      </ClerkLoading>
      <ClerkFailed>
        {worksWithoutClerk ? (
          <Outlet />
        ) : (
          <div className='flex min-h-screen flex-col items-center justify-center gap-3 bg-background p-8 text-center'>
            <ClerkFailedReport pathname={pathname} searchStr={searchStr} />
            <p className='text-base font-semibold text-foreground'>
              Sign-in couldn’t load
            </p>
            <p className='max-w-sm text-sm text-muted-foreground'>
              Check your connection, or pause an ad blocker for this site, then
              reload.
            </p>
            <button
              type='button'
              onClick={() => window.location.reload()}
              className='text-sm font-medium text-primary hover:underline'
            >
              Reload
            </button>
          </div>
        )}
      </ClerkFailed>
      <Show
        when='signed-in'
        fallback={
          hasClerkTicket ? (
            <div className='flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-white to-slate-50'>
              <div className='flex flex-col items-center gap-3'>
                <div className='h-8 w-8 animate-spin rounded-full border-4 border-[#3E8BFF] border-t-transparent'></div>
                <p className='text-sm text-slate-600'>Signing you in...</p>
              </div>
            </div>
          ) : isAuthRoute || isPublicRoute ? (
            <Outlet />
          ) : (
            <AuthRouter />
          )
        }
      >
        <TrialAccessBoundary
          bypass={bypassesTrialBoundary}
          deferTrialOffer={isOnboardingRoute}
        >
          {/* Fail-open: the boundary renders the app immediately and swaps in
              the mandatory plan chooser only when the entitlements payload
              says the trial ended without a decision. Orgs that never had a
              trial boot with zero added frames. */}
          <MobileBlocker>
            <CommandPalette />
            {isOnboardingRoute || isPublicRoute || isAuthRoute ? (
              <Outlet />
            ) : (
              // The shell's org-scoped queries 401 for a user with no
              // workspace yet (a fresh sign-up landing on /), so it waits
              // for the onboarding redirect / org activation first.
              <OnboardingCheck>
                <AppShell>
                  <Outlet />
                </AppShell>
              </OnboardingCheck>
            )}
          </MobileBlocker>
        </TrialAccessBoundary>
      </Show>
    </>
  );
};

/** Counts the sign-in failure screen: Clerk's own load failures mostly never
 *  get reported, so this is the only measure of how often people see it. */
function ClerkFailedReport({
  pathname,
  searchStr,
}: {
  pathname: string;
  searchStr: string;
}) {
  const posthog = usePostHog();
  useEffect(() => {
    // The sanitized URL, as $pageview sends it: the raw one can carry Clerk
    // and OAuth tokens.
    posthog?.capture('clerk_failed_to_load', {
      pathname,
      $current_url: pageviewUrl(window.origin, pathname, searchStr),
    });
  }, [posthog, pathname, searchStr]);
  return null;
}

/**
 * Pages are code-split, and the router keeps the old page on screen until the
 * new page's chunks load. On a cold cache that took seconds with no feedback,
 * so a click on a list looked like it did nothing while the URL had already
 * changed. A thin bar shows the navigation is underway; the delay keeps it
 * from flashing on warm, instant navigations.
 */
function RouteLoadingBar() {
  const isLoading = useRouterState({ select: (s) => s.isLoading });
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!isLoading) return setVisible(false);
    const timer = setTimeout(() => setVisible(true), 150);
    return () => clearTimeout(timer);
  }, [isLoading]);
  if (!visible) return null;
  return (
    <div
      role='progressbar'
      aria-label='Loading page'
      className='fixed inset-x-0 top-0 z-[100] h-0.5 animate-pulse bg-primary'
    />
  );
}

const Root = () => {
  return (
    <Provider store={store}>
      <RouteLoadingBar />
      <MaintenanceGate>
        <RootContent />
      </MaintenanceGate>
    </Provider>
  );
};

export const Route = createRootRoute({
  component: () => <Root />,
  errorComponent: RootError,
  onCatch: (error, info?: ErrorInfo) => {
    reportError(error, {
      tags: { surface: 'app_shell', boundary: 'root_route' },
      extra: { componentStack: info?.componentStack },
    });
  },
});
